import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ScanText, Save, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const PageTextPanel = ({ bookId, page }) => {
  const [text, setText] = useState(null);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState("");
  const apply = (d) => { setText(d.lines.map((l) => l[4]).join("\n") || d.text || ""); setCount(d.lines.length); };

  useEffect(() => {
    setText(null);
    api.get(`/books/${bookId}/pages/${page}/text`).then((r) => apply(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [bookId, page]);

  const save = async () => {
    setBusy("save");
    try { const { data } = await api.put(`/books/${bookId}/pages/${page}/text`, { lines: text.split("\n") }); apply(data); toast.success("Page text saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const scan = async () => {
    setBusy("ocr");
    try { const { data } = await api.post(`/books/${bookId}/pages/${page}/ocr`); apply(data); toast.success(data.lines.length ? `Found ${data.lines.length} lines of text` : "No text found on this page"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (text === null) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#64748B]" /></div>;
  const lines = text.split("\n").filter((t) => t.trim()).length;
  return (
    <div className="space-y-3" data-testid="page-text-panel">
      <div>
        <p className="font-display text-lg font-bold">Page {page} text</p>
        <p className="text-[11px] leading-relaxed text-[#64748B]">This text powers read-aloud, highlighting and search. One line per row; keep the same number of rows to preserve exact highlight positions.</p>
      </div>
      <textarea wrap="off" value={text} onChange={(e) => setText(e.target.value)} rows={14} placeholder="No text on this page yet — scan it or type it here." className="neu-inset w-full resize-y rounded-2xl p-3 font-mono text-[11px] leading-relaxed focus:outline-none" data-testid="page-text-input" />
      <p className={`font-mono text-[10px] ${count && lines !== count ? "text-[#F7933E]" : "text-[#94A3B8]"}`} data-testid="page-text-count">{lines} rows{count ? ` · ${count} detected` : ""}{count && lines !== count ? " · positions will be approximated" : ""}</p>
      <div className="flex gap-2">
        <button onClick={scan} disabled={!!busy} className="neu-btn flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-xs font-semibold disabled:opacity-60" data-testid="page-text-ocr-btn">
          {busy === "ocr" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanText className="h-3.5 w-3.5" />}Scan text (OCR)
        </button>
        <button onClick={save} disabled={!!busy} className="neu-btn-primary flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-xs font-bold disabled:opacity-60" data-testid="page-text-save-btn">
          {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}Save text
        </button>
      </div>
    </div>
  );
};
